<template>
  <li :class="getType">
    <span v-if="getListBlocks.content" class="list__block" v-html="getListBlocks.content"></span>
    <a v-else @click="addProductClick()">
      <span class="product__square image__holder">
        <span v-if="product.action_label" class="product__label">{{ product.action_label }}</span>
        <span v-else-if="showDiscountBadge" class="discount__label">{{ getDiscount }}%</span>
        <a :href="productHref">
          <img v-lazy="listItemImage" :alt="product.name" height="336" width="336"/>
        </a>
      </span>
      <span class="product__holder">
        <a :href="productHref">
          <span class="product__name">
            <h2>{{ product.name }}</h2>
          </span>
        </a>
        <span class="product__meta">
          <span class="product__stock_msg" v-show="product.sw_stock > 0 && (!product.hasOwnProperty('is_ppc') || product.is_ppc === false)">Op voorraad, direct verzonden</span>
          <span v-if="product['manufacturer']" class="product__brand">Merk: {{ product['manufacturer'] }}</span>
        </span>
        <span class="product__options">
          <label
            v-for="(configurable, index) in product.configurable_data"
            v-if="checkIfShown(configurable)"
            v-bind:key="index"
            @click.stop
          >
            <select :value="selectedOptions[index]" @change="setValue($event, index)" @click.stop>
              <option v-for="options in configurable.options" :value="options.id">
                {{ options.label }}
              </option>
            </select>
          </label>
        </span>
        <span class="price__holder">
          <span class="_from hide-for-small-only">Vanaf</span>
          <template v-if="Object.keys(selectedProduct).length === 0">
            <span v-if="product['final_price_incl_tax'] !== product['price_incl_tax']" class="price__old-price">{{ product.price_incl_tax.priceFormat() }}</span>
            <span class="price">{{ product['final_price_incl_tax'].priceFormat() }}</span>
          </template>
          <template v-if="Object.keys(selectedProduct).length > 0">
                      <span v-if="selectedProduct['price_incl_tax'] && selectedProduct['price_incl_tax'] !== selectedProduct['final_price_incl_tax']" class="price__old-price">{{ selectedProduct['price_incl_tax'].priceFormat() }}</span>
                      <span class="price">{{ this.selectedProduct['final_price_incl_tax'].priceFormat() }}</span>
          </template>

          <span v-if="!addedToCart" class="price__tax">{{ $t('inclusief btw') }}</span>
          <transition name="fade">
            <span v-if="addedToCart" key="added" class="added__message">{{ $t('Product has been added') }}</span>
          </transition>

          <div v-if="!outOfStockMessage" class="list-item__cart-wrap">
            <button
              type="button"
              :class="{ _loading: addToCartIsLoading, _added: addedToCart }"
              :disabled="addToCartIsLoading"
              class="_button _cta _cart"
              @click.stop.prevent="onListCartClick()"
            ></button>
            <transition name="fade">
              <div
                v-show="showPackCartTooltip"
                class="list-item__cart-tooltip"
                role="tooltip"
              >
                {{ $t('Vloeren zijn alleen per pak te bestellen, kies een andere maat.') }}
              </div>
            </transition>
          </div>
        </span>
        <transition name="fade">
          <span v-if="addToCartFailed" key="error" class="error__message">{{ addToCartErrorMessage }}</span>
        </transition>
      </span>
    </a>
  </li>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch} from 'vue-property-decorator';
import {enhancedEcommerce} from '../../shared/helpers/enhanced-ecommerce';
import {getProductDiscountPercent, productHasDiscount} from '../../shared/helpers/discount';
import {
  isConfigurableListItemSaleable,
  isPackSizeOneSquareMeter,
  mergeSelectedOptions,
  selectedOptionIsPackSize,
} from '../../shared/helpers/list-item-cart';
import {CartEvents, SolarDataLayer} from '@flashpointbv/solar-datalayer/dist';
import {KlaviyoTracking} from '../../shared/helpers/klaviyo';

@Component({
  name: 'catalog-product-list-item'
})

export default class extends Vue {
  @Prop({required: true}) readonly dataSource: string;
  @Prop({required: true}) readonly product: object;
  @Prop({default: 0}) readonly listIndex!: number;
  @Prop({default: false}) readonly type!: string;

  private addToCartIsLoading: boolean = false;
  private addedToCart: boolean = false;
  private addToCartFailed: boolean = false;
  private addToCartErrorMessage: string = '';
  private showPackCartTooltip: boolean = false;
  private _packTooltipTimer: number = 0;
  private selectedOptions: number[] = [];
  private selectedProduct: Object = {};

  protected get showDiscountBadge(): boolean {
    return productHasDiscount(this.discountProduct);
  }

  protected get getDiscount(): string {
    return getProductDiscountPercent(this.discountProduct);
  }

  /** Parent/indexed prices — same source as the PDP discount badge. */
  private get discountProduct(): Record<string, any> {
    return this.product as Record<string, any>;
  }

  /** url_key may already include a leading slash from Solr. */
  private get productHref(): string {
    const urlKey = String(this.product['url_key'] || '').replace(/^\/+/, '');
    return urlKey ? `/${urlKey}` : '#';
  }

  private get getType(): string {
    switch (this.type) {
      case 'recent':
      case 'upsell':
      case 'crosssell':
        return '';
      default:
        return 'small-12 medium-8 large-6 cell';
    }
  }

  private get listItemImage(): string {
    const image = this.product['image'];
    if (typeof image === 'string' && image && !image.startsWith('http')) {
      return `/media/catalog/product${image}`;
    }

    const plp = String(this.product['default_plp_image'] || '');
    const marker = '/catalog/product';
    const markerIndex = plp.indexOf(marker);
    if (plp.includes('/cdn/') && markerIndex !== -1) {
      return `/media/catalog/product${plp.slice(markerIndex + marker.length)}`;
    }

    return plp;
  }

  private get getListBlocks() {
    const blocks = this.$store.state.CatalogCategoryList.listBlocks.filter(o => o.index === this.listIndex);
    return blocks.length ? blocks[0] : '';
  }

  private get isSaleable(): boolean {
    if (this.product['product_type'] !== 'configurable') {
      return true;
    }

    const options = this.buildConfigurableItemOptions();
    const configurableData = this.product['configurable_data'] || [];

    return isConfigurableListItemSaleable(
      this.selectedProduct as Record<string, unknown>,
      Boolean(this.product['in_stock']),
      options.length,
      configurableData.length
    );
  }

  private get isPackOnlyOneSquareMeter(): boolean {
    return isPackSizeOneSquareMeter(this.selectedProduct as Record<string, unknown>)
      || selectedOptionIsPackSize(
        this.product['configurable_data'],
        this.selectedOptions
      );
  }

  private get outOfStockMessage(): boolean {
    return this.product.hasOwnProperty('out_of_stock_message') &&
      this.product['out_of_stock_message'];
  }

  private onListCartClick(): void {
    if (this.outOfStockMessage || this.addToCartIsLoading) {
      return;
    }
    this.seedSelectedOptions();
    this.resolveSelectedProduct();
    if (this.isPackOnlyOneSquareMeter) {
      clearTimeout(this._packTooltipTimer);
      this.showPackCartTooltip = true;
      this._packTooltipTimer = window.setTimeout(() => {
        this.showPackCartTooltip = false;
      }, 5000);
      return;
    }
    if (!this.isSaleable) {
      return;
    }
    this.addToCart();
  }

  private async addToCart(): Promise<any> {
    try {
      this.addToCartIsLoading = true;

      let productPayload = {
        sku: this.product['sku'],
        qty: 1,
      };

      if (this.product['product_type'] === 'configurable') {
        const configurableItemOptions = this.buildConfigurableItemOptions();

        if (configurableItemOptions.length > 0) {
          productPayload = Object.assign(productPayload, {
            product_option: {
              extension_attributes: {
                configurable_item_options: configurableItemOptions,
              },
            },
          });
        }
      }

      await this.$store.dispatch('CheckoutQuote/addProductToQuote', productPayload);

      const addToCartError = this.$store.getters['CheckoutQuote/getAddToCartError'];
      if (addToCartError) {
        this.addToCartFailed = true;
        this.addToCartErrorMessage = addToCartError.message || String(this.$t('Could not add product to cart'));
        await this.$store.dispatch('CheckoutCart/setCartMessage', this.addToCartErrorMessage);
        this.addedToCartHandler();
        return;
      }

      this.addToCartFailed = false;
      this.addToCartErrorMessage = '';
      await this.$store.dispatch('CheckoutCart/setCartMessage', null);

      enhancedEcommerce.addProductAddToCart(this.product, 1);
      SolarDataLayer.init().addAddToCart(this.product, CartEvents.ADD, 1);
      await KlaviyoTracking.addToCart(this.product, 1);

      // Cart page items are Blade-rendered; reload so the list/totals catch up
      // (same approach as checkout-coupon-form reload-on-success).
      if (this.type === 'crosssell') {
        window.location.reload();
        return;
      }

      await this.$store.dispatch('CheckoutCart/showCartPopup', true);
      await this.$store.dispatch('CheckoutCart/showProductAdded', true);

      this.addedToCart = true;
      this.addedToCartHandler();
    } catch (e) {
      console.log(e);
    } finally {
      this.addToCartIsLoading = false;
    }
  }

  private setValue($event, index) {
    this.$set(this.selectedOptions, index, parseInt($event.target.value, 10));
    this.resolveSelectedProduct();
  }

  private checkIfShown(data): boolean {
    this.seedSelectedOptions();
    return data.options.length > 1;
  }

  /**
   * Auto-select the cheapest child so the list shows the vanaf price.
   * Magento still requires configurable_item_options; without a selection,
   * add-to-cart is a silent no-op.
   */
  private seedSelectedOptions(): void {
    if (this.product['product_type'] !== 'configurable' || !this.product['configurable_data']) {
      return;
    }

    mergeSelectedOptions(
      this.product['configurable_data'],
      this.selectedOptions,
      this.product['virtual_products']
    ).forEach((optionId, index) => {
      if (this.selectedOptions[index] !== optionId) {
        this.$set(this.selectedOptions, index, optionId);
      }
    });
  }

  private buildConfigurableItemOptions(): Array<{ option_id: number; option_value: number }> {
    this.seedSelectedOptions();

    const configurableData = this.product['configurable_data'] || [];
    const configurableItemOptions = [];

    configurableData.forEach((configurable, index) => {
      const optionValue = this.selectedOptions[index];
      if (optionValue === null || optionValue === undefined || Number.isNaN(Number(optionValue))) {
        return;
      }

      configurableItemOptions.push({
        option_id: configurable.id,
        option_value: Number(optionValue),
      });
    });

    return configurableItemOptions;
  }

  /**
   * Map selected configurable option ids to a child (virtual) product.
   * Solr children often expose option ids (e.g. sw_sizeruler_option_id) but not
   * the storefront option labels, so id matching must come before label matching.
   */
  private resolveSelectedProduct(): void {
    if (this.product['product_type'] !== 'configurable' || !this.product['configurable_data']) {
      return;
    }

    const products = this.product['virtual_products'] || [];
    const configurabledata = this.product['configurable_data'];
    const selectedIds = this.selectedOptions.filter((id) => id !== null && id !== undefined && !Number.isNaN(Number(id)));

    this.selectedProduct = {};

    if (selectedIds.length > 0) {
      const byOptionId = products.find((element) =>
        selectedIds.every((id) =>
          Number(element['sw_sizeruler_option_id']) === Number(id)
          || Object.values(element).some((val) => val == id)
        )
      );

      if (byOptionId) {
        this.selectedProduct = byOptionId;
        return;
      }

      // Legacy: match by option labels when children still carry them.
      const needles: string[] = [];
      configurabledata.forEach((element, index) => {
        if (this.selectedOptions[index] == null) {
          return;
        }
        const option = (element.options || []).find((opt) => opt.id == this.selectedOptions[index]);
        if (option?.label) {
          needles.push(option.label);
        }
      });

      if (needles.length > 0) {
        const byLabel = products.find((element) =>
          needles.every((needle) => Object.values(element).includes(needle))
        );
        if (byLabel) {
          this.selectedProduct = byLabel;
          return;
        }
      }
    }

    // Last resort: selected options exist (or only one choice) but docs lack
    // matchable fields — use the first in-stock child so ATC is not a silent no-op.
    if (
      selectedIds.length > 0
      || configurabledata.every((element) => (element.options || []).length === 1)
    ) {
      this.selectedProduct = products.find((element) => element['in_stock']) || products[0] || {};
      if (Object.keys(this.selectedProduct).length === 0 && this.product['in_stock']) {
        this.selectedProduct = {in_stock: true};
      }
    }
  }

  private setInitialSelectedProduct(): void {
    this.selectedOptions = [];
    this.selectedProduct = {};
    this.seedSelectedOptions();
    this.resolveSelectedProduct();
  }

  @Watch('product')
  private onProductChange(): void {
    this.setInitialSelectedProduct();
  }

  private mounted(): void {
    this.setInitialSelectedProduct();
  }

  private addProductClick(): void {
    enhancedEcommerce.addProductClick(this.product, this.listIndex);
    SolarDataLayer.init().addSelectItem(this.product, this.listIndex, 'Product list');
  }

  private addedToCartHandler(): void {
    setTimeout(() => {
      this.showPackCartTooltip = false;
      this.addedToCart = false;
      this.addToCartFailed = false;
      this.addToCartErrorMessage = '';
    }, 4000);
  }

  private beforeDestroy(): void {
    clearTimeout(this._packTooltipTimer);
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
